import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { connector, environment } from '@env/environment';

import {
  ChangePasswordModel,
  ConfirmEmailModel,
  RegisteredUserModel,
  RegisterUserModel,
  ResetPasswordModel,
  RespConfirmModel,
  TokenModel,
  UpdateUserModel,
  UserBlockPossibilityModel,
  UserDeletionBlockersResultModel,
  UserModel,
} from '@data-layer/models/user';

@Injectable({
  providedIn: 'root',
})
export class UsersRepository {
  constructor(private apiService: ApiService) {}

  public get(isShowLoader?: boolean): Observable<UserModel> {
    return this.apiService.get(environment.url.user, null, { isShowLoader });
  }

  public update(user: UpdateUserModel): Observable<never> {
    return this.apiService.put(environment.url.user, user);
  }

  public create(user: RegisterUserModel, recaptchaToken: string): Observable<RegisteredUserModel> {
    return this.apiService.post(environment.url.user, user, {
      headers: {
        'x-recaptcha-token': recaptchaToken,
      },
    });
  }

  public confirmEmail(obj: ConfirmEmailModel): Observable<RespConfirmModel> {
    return this.apiService.post(`${environment.url.user}confirm-email`, obj);
  }

  public forgotPassword(email: string): Observable<never> {
    return this.apiService.get(`${environment.url.user}reset-password`, { params: { email } });
  }

  public resetPassword(resetPassword: ResetPasswordModel, recaptchaToken: string): Observable<UserModel> {
    return this.apiService.post(`${environment.url.user}reset-password`, resetPassword, {
      headers: {
        'x-recaptcha-token': recaptchaToken,
      },
    });
  }

  public resendConfirmationEmail(params: { email?: string; userId?: string }): Observable<never> {
    return this.apiService.get(`${environment.url.user}resend-confirmation-email`, { params });
  }

  public phoneConfirmation(phoneNumber: string): Observable<{ accepted: boolean }> {
    return this.apiService.post(`${environment.url.user}phone-confirmation`, null, { params: { phoneNumber } });
  }

  public confirmationPhoneCode(phoneNumber: string, code: string): Observable<{ valid: boolean }> {
    return this.apiService.put(`${environment.url.user}phone-confirmation`, null, { params: { phoneNumber, code } });
  }

  public login(obj: Object): Observable<TokenModel> {
    const { client_id, client_secret, grant_type } = environment;
    const data = { ...obj, client_id, client_secret, grant_type };

    const form_data = Object.keys(data)
      .map((key) => {
        return `${key}=${encodeURIComponent(data[key])}`;
      })
      .join('&');

    return this.apiService.post(
      connector,
      form_data,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      { isShowLoader: true },
    );
  }

  public refreshToken(refresh_token: String): Observable<TokenModel> {
    const { client_id, client_secret } = environment;
    const data = { client_id, client_secret, grant_type: 'refresh_token', refresh_token };
    const form_data = Object.keys(data)
      .map((key) => `${key}=${encodeURIComponent(data[key])}`)
      .join('&');

    return this.apiService.post(connector, form_data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public getDeletionBlockers(): Observable<UserDeletionBlockersResultModel> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/users/deletion-blockers`, null);
  }

  public delete(reason: string): Observable<never> {
    return this.apiService.request('delete', `${environment.url.prefixCustoms}v2/users`, { body: { reason } });
  }

  public changePassword(data: ChangePasswordModel): Observable<UserModel> {
    return this.apiService.put(`${environment.url.user}change-password`, data);
  }

  public isBlockAvailableForUser(): Observable<UserBlockPossibilityModel> {
    return this.apiService.get<UserBlockPossibilityModel>(`${environment.url.prefixCustoms}v2/users/block-possibility`, null);
  }

  public blockAccountByUser(isBlockedNote: string): Observable<any> {
    return this.apiService.request('delete', `${environment.url.prefixCustoms}v2/users`, { body: { isBlockedNote } });
  }

  public acceptInvitedUserEmail(token?: string): Observable<never> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
    return this.apiService.post(`${environment.url.user}invited-user-confirm-email`, {}, options);
  }

  public setUserCurrentOrganization(organizationId: number): Observable<never> {
    return this.apiService.patch<never>(`${environment.url.prefixIdentity}organizations/${organizationId}/default-id`);
  }

  public setUserCurrentCompany(companyId: number): Observable<never> {
    return this.apiService.patch<never>(`${environment.url.prefixIdentity}companies/${companyId}/default-id`);
  }
}
